import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, Loader2, ShoppingBag, ArrowLeft, Sparkles, Pencil, Calendar, Clock, Truck, MapPin, AlertCircle, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../contexts/CartContext";
import { useDeliveryConfig } from "../contexts/SiteTextsContext";
import CompleteTheLook from "../components/CompleteTheLook";
import CTLQuickView from "../components/CTLQuickView";
import CartTimer from "../components/CartTimer";
import UpsellBanner from "../components/UpsellBanner";
import { toast } from "sonner";
import { getDisplayName, getDisplayImage } from "../utils/variationDisplay";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

function getReturnDate(eventDate, duration) {
  if (!eventDate) return "-";
  const d = new Date(eventDate);
  d.setDate(d.getDate() + (duration || 4));
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

/* ─── Inline Booking Editor ─── */
function EditBookingInline({ item, onSave, onCancel, cartGroups }) {
  const [eventDate, setEventDate] = useState(item.event_date?.slice(0, 10) || "");
  const [duration, setDuration] = useState(item.duration || 4);
  const [delivery, setDelivery] = useState(item.delivery_method || "pickup");
  const [saving, setSaving] = useState(false);
  const [dateError, setDateError] = useState("");
  const PAST_DATE_MSG = "Please select today’s date or a future date.";
  const handleDateInvalid = (e) => { e.preventDefault(); e.target.setCustomValidity(PAST_DATE_MSG); setDateError(PAST_DATE_MSG); };
  const handleDateInput = (e) => { e.target.setCustomValidity(""); setDateError(""); };

  // Check for delivery window conflict - now using both event_date AND duration
  const allItems = cartGroups?.flatMap(g => [g.main, ...g.addons]) || [];
  const otherDeliveryItems = allItems.filter(i => i.id !== item.id && i.delivery_method === "delivery");

  // Section 5 — same-day / next-day is pickup-only. Force delivery back to pickup
  // whenever the customer picks such a date, and surface the reason inline.
  const isSameOrNextDayMel = (dateStr) => {
    if (!dateStr) return false;
    try {
      const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
      const nowMel = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
      const todayMel = new Date(nowMel.getFullYear(), nowMel.getMonth(), nowMel.getDate());
      const evMel = new Date(y, m - 1, d);
      const delta = Math.round((evMel - todayMel) / (1000 * 60 * 60 * 24));
      return delta >= 0 && delta <= 1;
    } catch { return false; }
  };
  const pickupOnlyForDate = isSameOrNextDayMel(eventDate);
  useEffect(() => {
    if (pickupOnlyForDate && delivery === "delivery") setDelivery("pickup");
  }, [pickupOnlyForDate]);

  // Build set of existing (event_date, duration) windows
  const existingWindows = otherDeliveryItems.map(i => ({
    date: i.event_date?.slice(0, 10) || "",
    duration: i.duration || 4
  }));
  
  // Check if current selection creates a new window
  const currentWindow = { date: eventDate, duration };
  const hasConflict = delivery === "delivery" && 
    existingWindows.length > 0 && 
    !existingWindows.some(w => w.date === currentWindow.date && w.duration === currentWindow.duration);

  // Group existing windows for display message
  const windowDescriptions = [...new Set(existingWindows.map(w => `${w.date} (${w.duration}-day)`))].join(", ");

  const handleSave = async () => {
    // Block past dates with branded inline error
    if (eventDate) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const picked = new Date(eventDate + "T00:00:00");
      if (picked < today) { setDateError(PAST_DATE_MSG); return; }
    }
    setDateError("");
    setSaving(true);
    const updates = {};
    if (eventDate !== (item.event_date?.slice(0, 10) || "")) updates.event_date = eventDate;
    if (duration !== item.duration) updates.duration = duration;
    if (delivery !== item.delivery_method) updates.delivery_method = delivery;
    if (Object.keys(updates).length === 0) { onCancel(); return; }
    await onSave(updates);
    setSaving(false);
  };

  return (
    <div className="bg-[#FAF8F0] border border-[#282828]/8 p-4 mt-3" data-testid={`edit-booking-${item.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Event Date</label>
          <input type="date" value={eventDate}
            onChange={(e) => { setEventDate(e.target.value); handleDateInput(e); }}
            onInvalid={handleDateInvalid}
            onInput={handleDateInput}
            min={new Date().toISOString().slice(0, 10)}
            className={`w-full border px-3 py-2 text-[12px] bg-white focus:outline-none ${dateError ? "border-[#B85C5C] focus:border-[#B85C5C]" : "border-[#282828]/12 focus:border-[#9A7B4F]"}`}
            data-testid={`edit-date-${item.id}`}
            aria-invalid={!!dateError}
            aria-describedby={dateError ? `edit-date-error-${item.id}` : undefined}
          />
          {dateError && (
            <p id={`edit-date-error-${item.id}`} role="alert" className="mt-1 text-[11px] text-[#B85C5C] leading-snug" data-testid={`edit-date-error-${item.id}`}>
              {dateError}
            </p>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Duration</label>
          <div className="flex gap-2">
            {[4, 8].map(d => (
              <button key={d} onClick={() => setDuration(d)}
                className={`flex-1 py-2 text-[11px] border transition-colors ${duration === d ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' : 'bg-white text-[#676664] border-[#282828]/12'}`}
                data-testid={`edit-duration-${d}-${item.id}`}>{d} days</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Delivery</label>
          <div className="flex gap-2">
            {[{ key: "pickup", label: "Pick Up" }, { key: "delivery", label: "Delivery" }].map(m => {
              const disabled = m.key === "delivery" && pickupOnlyForDate;
              return (
                <button
                  key={m.key}
                  onClick={() => !disabled && setDelivery(m.key)}
                  disabled={disabled}
                  className={`flex-1 py-2 text-[11px] border transition-colors ${delivery === m.key ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' : 'bg-white text-[#676664] border-[#282828]/12'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  data-testid={`edit-delivery-${m.key}-${item.id}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {pickupOnlyForDate && (
        <div className="flex items-start gap-2 mt-3 bg-[#FAF3E0] border border-[#E7CE9A] p-3" data-testid={`edit-pickup-only-banner-${item.id}`}>
          <AlertTriangle className="w-4 h-4 text-[#9A7B4F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[11px] text-[#3D2B1F] leading-relaxed">
            <strong>Pickup only:</strong> same-day and next-day event dates are collected in-store. Delivery isn&apos;t available for this date.
          </p>
        </div>
      )}
      {hasConflict && (
        <div className="flex items-start gap-2 mt-3 bg-[#FDF6EC] border border-[#E8D5B5] rounded p-3" data-testid="edit-delivery-conflict">
          <AlertTriangle className="w-4 h-4 text-[#9A7B4F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[11px] text-[#5A5A5A] leading-relaxed">
            This item ({eventDate}, {duration}-day) creates a separate delivery window from your other items ({windowDescriptions}). 
            This will add an extra $15 delivery fee. Match the event date and rental period, or switch to pickup to avoid the extra charge.
          </p>
        </div>
      )}
      <div className="flex gap-3 mt-3 justify-end">
        <button onClick={onCancel} className="px-4 py-1.5 text-[11px] text-[#676664] border border-[#282828]/12" data-testid={`edit-cancel-${item.id}`}>Cancel</button>
        <button onClick={handleSave} disabled={saving} className="px-6 py-1.5 bg-[#9A7B4F] text-white text-[11px] tracking-wider uppercase" data-testid={`edit-save-${item.id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
        </button>
      </div>
      <p className="text-[10px] text-[#9A7B4F]/60 mt-2 italic">Changes will automatically sync to all add-on items</p>
    </div>
  );
}

/* ─── Cart Item Card ─── */
function CartItemCard({ item, isAddon, onRemove, removing, onEdit, editingId, onSaveEdit, onCancelEdit, cartGroups }) {
  const tierDisc = item.tier_discount_amount || 0;
  const effectiveRental = item.rental_price - tierDisc;

  return (
    <div className={`bg-white ${isAddon ? 'border-l-2 border-l-[#9A7B4F]/30 border-t border-r border-b border-[#282828]/6' : 'border border-[#282828]/8'}`} data-testid={`cart-item-${item.id}`}>
      <div className="flex gap-0">
        {/* Large Product Image */}
        <Link to={`/outfit/${item.outfit_id}`} className="flex-shrink-0 block">
          <div className="w-[130px] h-[175px] sm:w-[160px] sm:h-[210px] bg-[#f5f3f0] overflow-hidden">
            {(() => { const _img = getDisplayImage(item, item); return _img ? (
              <img src={_img} alt={getDisplayName(item, item)} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            ) : null; })()}
          </div>
        </Link>

        {/* Item Details */}
        <div className="flex-1 min-w-0 p-4 sm:p-5 flex flex-col">
          {/* Header: Name + Remove */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link to={`/outfit/${item.outfit_id}`} className="text-[17px] sm:text-[19px] text-[#1A1A1A] hover:text-[#9A7B4F] leading-snug block truncate" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                {getDisplayName(item, item)}
              </Link>
            </div>
            <button onClick={() => onRemove(item.id)} disabled={removing === item.id}
              className="p-1.5 text-[#676664]/30 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`remove-cart-${item.id}`}>
              {removing === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>

          {/* Booking Info Row */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] sm:text-[12px] text-[#5A5A5A]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} />
              {formatDate(item.event_date)} &rarr; {getReturnDate(item.event_date, item.duration)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} />
              {item.duration} days
            </span>
            <span className="inline-flex items-center gap-1.5">
              {item.delivery_method === "delivery" ? <Truck className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} /> : <MapPin className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} />}
              {item.delivery_method === "delivery" ? "Delivery" : "Pick Up"}
            </span>
          </div>

          {/* Price Breakdown */}
          <div className="mt-auto pt-3">
            <div className="border-t border-[#282828]/6 pt-3 space-y-1.5">
              <div className="flex justify-between items-center text-[12px] sm:text-[13px]">
                <span className="text-[#5A5A5A]">Rental Fee</span>
                <div className="flex items-center gap-2">
                  {tierDisc > 0 && (
                    <span className="text-[11px] text-[#676664]/40 line-through">${item.rental_price.toFixed(2)}</span>
                  )}
                  <span className="text-[#282828] font-medium">${effectiveRental.toFixed(2)}</span>
                </div>
              </div>
              {tierDisc > 0 && (
                <div className="flex justify-between items-center text-[11px]">
                  <span className="text-[#9A7B4F]">Multi-Item Discount ({item.tier_discount_percent}%)</span>
                  <span className="text-[#9A7B4F] font-medium">-${tierDisc.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-[12px] sm:text-[13px]">
                <span className="text-[#5A5A5A]">Security Bond* <span className="text-[10px] text-[#676664]/40">(refundable)</span></span>
                <span className="text-[#282828] font-medium">${item.bond_amount.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-[#676664]/45 italic leading-snug pl-0.5">Your bond is 100% refunded within 48 hours of your outfit&apos;s safe return.</p>
            </div>
          </div>

          {/* Edit button */}
          {!isAddon && (
            <button onClick={() => onEdit(item.id)}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#9A7B4F]/60 hover:text-[#9A7B4F] transition-colors"
              data-testid={`edit-btn-${item.id}`}>
              <Pencil className="w-3 h-3" /> Edit Booking
            </button>
          )}
        </div>
      </div>

      {/* Inline Editor */}
      {editingId === item.id && (
        <div className="px-4 pb-4">
          <EditBookingInline item={item} onSave={onSaveEdit} onCancel={onCancelEdit} cartGroups={cartGroups} />
        </div>
      )}
    </div>
  );
}

/* ─── Main Cart Page ─── */
export default function CartPage() {
  const navigate = useNavigate();
  const { fetchCart, cartGroups, cartTotals, cartCount, removeFromCart, updateCartItem, addToCart, discountInfo } = useCart();
  const { auspost_content_visible } = useDeliveryConfig();
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [quickViewOutfit, setQuickViewOutfit] = useState(null);

  useEffect(() => { fetchCart().finally(() => setLoading(false)); }, [fetchCart]);

  const handleRemove = async (itemId) => {
    setRemoving(itemId);
    await removeFromCart(itemId);
    await fetchCart();
    setRemoving(null);
  };

  const mainOutfitId = cartGroups.length > 0 ? cartGroups[0].main.outfit_id : null;
  const mainItem = cartGroups.length > 0 ? cartGroups[0].main : null;

  // Check if all items have required booking data (dates + delivery method)
  const allItemsReady = cartGroups.length > 0 && cartGroups.every(g => {
    const all = [g.main, ...g.addons];
    return all.every(item => item.event_date && item.delivery_method);
  });

  const handleAddCTLItem = async (item) => {
    if (!mainItem) return;
    try {
      const option = item.selectedOption || item.rental_options?.[0];
      const rentalPrice = option?.price_4_day || option?.price_8_day || option?.price || item.price_4_day || item.price_8_day || 0;
      const discountPercent = item.discountPercent || 0;
      await addToCart({
        outfit_id: item.id,
        outfit_name: item.name,
        outfit_image: item.images?.[0] || "",
        event_date: mainItem.event_date,
        duration: mainItem.duration,
        delivery_method: mainItem.delivery_method,
        rental_price: rentalPrice,
        bond_amount: item.bond_amount || item.deposit || 0,
        rental_option_id: option?.id || null,
        rental_option_name: option?.name || null,
        parent_cart_item_id: mainItem.id,
        is_addon: true,
        addon_discount_percent: discountPercent,
      });
      toast.success(`${item.name} added to your look`);
      await fetchCart();
    } catch {
      toast.error("Failed to add item");
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-[1280px] w-full mx-auto px-4 lg:px-8 py-6 lg:py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-[#676664]/40 hover:text-[#676664] transition-colors" data-testid="cart-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl lg:text-3xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Your Cart
            </h1>
            {cartCount > 0 && (
              <span className="text-[11px] text-[#676664]/60 bg-[#282828]/5 px-2.5 py-0.5 rounded-full">{cartCount} {cartCount === 1 ? 'item' : 'items'}</span>
            )}
          </div>
          {cartCount > 0 && <CartTimer />}
        </div>

        {/* Upsell banner */}
        {cartCount > 0 && discountInfo?.next_tier && (
          <div className="mb-6">
            <UpsellBanner />
          </div>
        )}

        {/* Free Delivery Banners — per-window aware */}
        {cartCount > 0 && (() => {
          const windows = cartTotals.delivery_windows || [];
          const hasDeliveryWindows = windows.length > 0;
          const allFree = hasDeliveryWindows && windows.every(w => w.free_delivery);
          const anyPaid = hasDeliveryWindows && windows.some(w => !w.free_delivery);
          const savings = cartTotals.delivery_savings || 0;

          if (!hasDeliveryWindows) return null; // all pickup — no delivery banners

          if (allFree && savings > 0) {
            return (
              <div className="mb-6 bg-[#F0F6F2] border border-[#5A7B5F] rounded-lg p-4" data-testid="free-delivery-achieved">
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-[#5A7B5F] flex-shrink-0" strokeWidth={1.5} />
                  <p className="text-[13px] text-[#2D3A30] font-medium">
                    You&apos;ve unlocked FREE delivery! <span className="text-[#5A7B5F]">(saving ${savings.toFixed(2)})</span>
                  </p>
                </div>
              </div>
            );
          }

          if (anyPaid && windows.length > 1) {
            return (
              <div className="mb-6 bg-[#FDF6EC] border border-[#E8D5B5] rounded-lg p-4" data-testid="delivery-window-warning">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#9A7B4F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[13px] text-[#282828] font-medium mb-1">Multiple delivery windows</p>
                    <p className="text-[12px] text-[#5A5A5A] leading-relaxed">
                      Your cart has items across {windows.length} delivery windows.
                      {windows.filter(w => !w.free_delivery).length > 0 && ` ${windows.filter(w => !w.free_delivery).length} window(s) incur a $${(cartTotals.delivery_fee_per_window || 15).toFixed(0)} delivery fee each.`}
                      {' '}Match event dates and rental periods, or switch to pickup to reduce fees.
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })()}

        {cartGroups.length === 0 ? (
          <div className="text-center py-24" data-testid="cart-empty">
            <ShoppingBag className="w-14 h-14 text-[#676664]/15 mx-auto mb-5" strokeWidth={1} />
            <p className="text-[18px] text-[#676664]/50 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Your cart is empty</p>
            <p className="text-[13px] text-[#676664]/35 mb-6">Discover pieces to borrow for your next occasion</p>
            <Link to="/" className="inline-block text-[11px] tracking-[0.12em] uppercase text-[#9A7B4F] border-b border-[#9A7B4F]/30 pb-0.5 hover:border-[#9A7B4F] transition-colors" data-testid="cart-browse-link">
              Browse Collection
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start overflow-hidden" data-testid="cart-layout">

            {/* ─── LEFT COLUMN ─── */}
            <div className="min-w-0">
              {/* Cart Items */}
              <div className="space-y-4" data-testid="cart-items">
                {cartGroups.map((group) => (
                  <div key={group.main.id}>
                    <CartItemCard
                      item={group.main}
                      isAddon={false}
                      onRemove={handleRemove}
                      removing={removing}
                      onEdit={(id) => setEditingId(editingId === id ? null : id)}
                      editingId={editingId}
                      onSaveEdit={async (updates) => {
                        await updateCartItem(group.main.id, updates);
                        await fetchCart();
                        setEditingId(null);
                        toast.success(group.addons.length > 0 ? "Booking updated — add-ons synced" : "Booking updated");
                      }}
                      onCancelEdit={() => setEditingId(null)}
                      cartGroups={cartGroups}
                    />

                    {/* Add-on Items */}
                    {group.addons.map((addon) => (
                      <div key={addon.id} className="mt-2 ml-4 sm:ml-8">
                        <CartItemCard
                          item={addon}
                          isAddon={true}
                          onRemove={handleRemove}
                          removing={removing}
                          onEdit={() => {}}
                          editingId={null}
                          onSaveEdit={() => {}}
                          onCancelEdit={() => {}}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* ─── Complete the Look: full-width horizontal upsell ─── */}
              {mainOutfitId && (
                <div className="mt-6 pt-6 border-t border-[#282828]/10" data-testid="cart-ctl-upsell">
                  <CompleteTheLook
                    outfitId={mainOutfitId}
                    onAddBundle={handleAddCTLItem}
                    onQuickView={(item) => setQuickViewOutfit(item)}
                    mainDuration={mainItem?.duration || 4}
                    variant="cart"
                  />
                </div>
              )}
            </div>

            {/* ─── RIGHT COLUMN: Order Summary ─── */}
            <div className="lg:sticky lg:top-24 self-start min-w-0 w-full max-w-full">
              <div className="bg-white border border-[#282828]/8" data-testid="cart-summary">
                <div className="px-5 py-4 border-b border-[#282828]/8">
                  <h3 className="text-[15px] text-[#282828] tracking-[0.06em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    Order Summary
                  </h3>
                </div>

                <div className="px-5 py-4">
                  {/* Discount tier info */}
                  {(discountInfo?.discount_percent || 0) > 0 && (
                    <div className="mb-4 px-3 py-2 bg-[#F5F0E6] border border-[#9A7B4F]/15 text-[11px] text-[#9A7B4F]" data-testid="discount-tier-badge">
                      <Sparkles className="w-3 h-3 inline mr-1.5" strokeWidth={1.5} />
                      {discountInfo.discount_percent}% multi-item discount applied
                    </div>
                  )}

                  {/* Per-item breakdown */}
                  <div className="space-y-4 mb-5">
                    {cartGroups.map(group => {
                      const allItems = [group.main, ...group.addons];
                      return allItems.map(item => {
                        const tierDisc = item.tier_discount_amount || 0;
                        return (
                          <div key={item.id} className="pb-3 border-b border-[#282828]/5 last:border-0 last:pb-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-[12px] text-[#282828] font-medium truncate">{getDisplayName(item, item)}</p>
                              </div>
                            </div>
                            <div className="mt-1.5 space-y-1">
                              <div className="flex justify-between text-[11px]">
                                <span className="text-[#676664]">Rental</span>
                                <div className="flex items-center gap-1.5">
                                  {tierDisc > 0 && <span className="text-[10px] text-[#676664]/35 line-through">${item.rental_price.toFixed(2)}</span>}
                                  <span className="text-[#282828]">${(item.rental_price - tierDisc).toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="flex justify-between text-[11px]">
                                <span className="text-[#676664]">Security Bond*</span>
                                <span className="text-[#282828]">${item.bond_amount.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })}
                  </div>

                  {/* Totals */}
                  <div className="border-t border-[#282828]/10 pt-4 space-y-2 text-[13px]">
                    <div className="flex justify-between">
                      <span className="text-[#5A5A5A]">Rental Total</span>
                      <span className="text-[#282828] font-medium">${(cartTotals.rental || 0).toFixed(2)}</span>
                    </div>
                    {(cartTotals.discount || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-[#9A7B4F]">Multi-Item Discount ({discountInfo?.discount_percent || 0}%)</span>
                        <span className="text-[#9A7B4F] font-medium">-${(cartTotals.discount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[#5A5A5A]">Security Bonds*</span>
                      <span className="text-[#282828] font-medium">${(cartTotals.bond || 0).toFixed(2)}</span>
                    </div>
                    <p className="text-[10px] text-[#676664]/45 italic leading-snug">Your bond is 100% refunded within 48 hours of your outfit&apos;s safe return.</p>
                    {/* Delivery fee display — per-window breakdown */}
                    {(cartTotals.delivery_windows || []).length > 0 && (
                      <div className="space-y-1">
                        {cartTotals.delivery_windows.map((w, idx) => (
                          <div key={`${w.event_date}-${w.duration}-${idx}`} className="flex justify-between">
                            <span className={w.free_delivery ? "text-[#5A7B5F]" : "text-[#5A5A5A]"}>
                              Delivery ({new Date(w.event_date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" })}, {w.duration}-day)
                            </span>
                            {w.free_delivery ? (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-[#676664]/40 line-through">${(cartTotals.delivery_fee_per_window || 15).toFixed(2)}</span>
                                <span className="text-[#5A7B5F] font-medium">FREE</span>
                              </div>
                            ) : (
                              <span className="text-[#282828] font-medium">${w.fee.toFixed(2)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Return info */}
                    {auspost_content_visible && (
                      <p className="text-[9px] text-[#676664]/35 pt-1 italic">
                        Prepaid Australia Post return label included with your order
                      </p>
                    )}
                    <div className="flex justify-between items-baseline pt-3 mt-2 border-t border-[#282828]/10">
                      <span className="text-[#282828] font-semibold text-[14px]">Total Due</span>
                      <span className="text-[#9A7B4F] font-bold text-[22px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>${(cartTotals.grand_total || 0).toFixed(2)}</span>
                    </div>
                    <p className="text-[10px] text-[#676664]/40 pt-1">
                      Includes ${(cartTotals.refundable || 0).toFixed(2)} refundable security bond*
                    </p>
                    <p className="text-[9px] text-[#676664]/30 pt-2 border-t border-[#282828]/5 mt-2">
                      *Fully refundable upon return in original condition
                    </p>
                  </div>
                </div>

                <div className="px-5 pb-5">
                  <Button
                    onClick={() => {
                      if (!allItemsReady) {
                        toast.error("Please select rental dates and delivery option for all items");
                        return;
                      }
                      navigate("/checkout");
                    }}
                    disabled={!allItemsReady}
                    className={`w-full py-5 text-[11px] tracking-[0.15em] uppercase font-medium ${allItemsReady ? 'bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white' : 'bg-[#282828]/10 text-[#676664]/40 cursor-not-allowed'}`}
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                  </Button>
                  {!allItemsReady && (
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-[#9A7B4F]/70" data-testid="checkout-blocked-msg">
                      <AlertCircle className="w-3 h-3 flex-shrink-0" />
                      <span>Select rental dates & delivery for all items to continue</span>
                    </div>
                  )}
                  <Link to="/" className="block text-center mt-3 text-[11px] text-[#676664]/50 hover:text-[#9A7B4F] tracking-[0.06em] transition-colors" data-testid="continue-shopping-link">
                    Continue Browsing
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {quickViewOutfit && mainItem && (
        <CTLQuickView
          outfit={quickViewOutfit}
          isOpen={!!quickViewOutfit}
          onClose={() => setQuickViewOutfit(null)}
          onAddBundle={handleAddCTLItem}
          parentOutfitId={mainItem.outfit_id}
          bookingParams={{
            eventDate: mainItem.event_date ? new Date(mainItem.event_date) : null,
            duration: mainItem.duration,
            deliveryMethod: mainItem.delivery_method,
          }}
        />
      )}
      <Footer />
    </div>
  );
}
